import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiCheckSquare } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';
import { useUpdateSkillModalitiesMutation } from '@/app/services/nucleus';
import { useNotification } from '@/hooks/useNotification';
import LoaderOverlay from '../common/LoaderOverlay';
import ModalComponent from '@/features/common/ModalComponent';
import FormRow from '@/features/user/FormRow';

const EditSkillModalities = ({ isOpen, onClose, skill, modalityOptions = [] }) => {
  const initialRef = useRef(null);
  const finalRef = useRef(null);

  const [modalities, setModalities] = useState([]);
  const confirmClear = useDisclosure();

  const [updateSkillModalities, { isLoading, isError, error, isSuccess, reset }] = useUpdateSkillModalitiesMutation();

  useEffect(() => {
    if (isOpen) {
      setModalities((skill?.modalities || []).map((m) => ({ value: m.id, label: m.short_name })));
    } else {
      setModalities([]);
      confirmClear.onClose();
      reset();
    }
  }, [isOpen, skill]);

  useNotification(isSuccess, onClose, isSuccess, 'success', 'Modalities updated');
  useNotification(isError, undefined, isError, 'error', error?.data?.detail ?? 'Error updating modalities');

  const performSave = () => {
    updateSkillModalities({ skillId: skill?.id, modalityIds: modalities.map((m) => m.value) });
  };

  const handleSave = () => {
    if (modalities.length === 0) {
      confirmClear.onOpen();
      return;
    }
    performSave();
  };

  const handleConfirmClear = () => {
    confirmClear.onClose();
    performSave();
  };

  return (
    <>
      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="2xl"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <FiCheckSquare />
              <Text>Edit Modalities</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isLoading} />
          <ModalBody pb={6}>
            <VStack spacing={4} position="relative" align="stretch">
              <LoaderOverlay loading={isLoading} />
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />A skill&apos;s wording is fixed at creation. You can change its modalities here; saving replaces the skill&apos;s current
                modalities.
              </Alert>
              <FormRow id="skill-description" label="Description" templateColumns="110px auto" gap={0} width="100%">
                <Text aria-label="skill-description" ref={initialRef} tabIndex={-1}>
                  {skill?.description}
                </Text>
              </FormRow>
              <FormRow id="skill-category" label="Category" templateColumns="110px auto" gap={0} width="100%">
                <Text aria-label="skill-category">{skill?.category}</Text>
              </FormRow>
              <FormControl aria-label="modalities-control">
                <FormRow id="modalities" label="Modalities" templateColumns="110px auto" gap={0} width="100%">
                  <Select
                    id="modalities"
                    aria-label="modalities"
                    chakraStyles={chakraStyles}
                    focusBorderColor="brand.700"
                    options={modalityOptions}
                    value={modalities}
                    onChange={(opts) => setModalities(opts || [])}
                    isMulti
                    isClearable
                    isSearchable
                    isDisabled={isLoading}
                    colorScheme="blue"
                    placeholder="Select modalities"
                  />
                </FormRow>
              </FormControl>
              <HStack width="100%" py={3} justifyContent="end">
                <Button onClick={onClose} isDisabled={isLoading}>
                  Cancel
                </Button>
                <Button variant="purple" onClick={handleSave} isLoading={isLoading}>
                  Save
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      <ModalComponent
        size="sm"
        title="Clear all modalities"
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={handleConfirmClear}
        isOpen={confirmClear.isOpen}
        onClose={confirmClear.onClose}
        isError={false}
      >
        <Box>
          <Text>
            This will remove <strong>all</strong> modalities from <strong>{skill?.description}</strong>. Are you sure you want to continue?
          </Text>
        </Box>
      </ModalComponent>
    </>
  );
};

EditSkillModalities.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  skill: PropTypes.shape({
    id: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    modalities: PropTypes.array,
  }),
  modalityOptions: PropTypes.array,
};

export default EditSkillModalities;
